/**
 * THE LEGAL DOCUMENTS, AS DATA.
 *
 * ── WHY THIS FILE EXISTS ──────────────────────────────────────────────────
 * The checkout shows a buyer the Enterprise Subscription Terms and the Data
 * Sharing Addendum in a pane, names both by version in the acceptance box,
 * and links to "the full document" at a permanent versioned address. Until
 * Sep 9 2026 those addresses returned the app shell with HTTP 200: a buyer
 * clicking through to the terms they were accepting landed on the app. The
 * pane's text was real, assembled from the commitments pricing.ts publishes,
 * but there was no canonical document behind it.
 *
 * This file is that document. Both agreements live here as structured data,
 * built from the SAME constants the pricing page, the trust page and the
 * checkout pane render, so a number cannot differ between the contract and
 * the page a buyer read before accepting it. The Worker renders them to HTML
 * at /legal/<document>/<version> (src/worker/routes/legal.ts); the checkout
 * pane projects its excerpt FROM them (`paneClauses`), so the excerpt is by
 * construction a subset of the document it excerpts.
 *
 * ── VERSIONS ARE IMMUTABLE ────────────────────────────────────────────────
 * A published version is never edited. Its text is hashed (`digest`) and the
 * hash is pinned in PUBLISHED_DIGESTS; a test fails if the rendered text of a
 * published version drifts from its pin. Any change to a published version is
 * a NEW version with a new slug, and the old one stays at its own address, so
 * P3 can always prove what an institution accepted and on what date. A DRAFT
 * version carries no pin and no effective date, and the rendered page says
 * plainly that it is not in force.
 *
 * ── STATUS OF THESE TEXTS ─────────────────────────────────────────────────
 * 2026.1 of both was published Sep 15 2026 and 2026.2 on Sep 17 2026 (the
 * roster of who has joined; section 6 below). The paragraph that follows is
 * the drafting history. Both 2026.1 documents were DRAFTS. They were drafted from the click-through
 * legal package spec of Sep 7 2026 (Docs/P3 Enterprise/Spec, Click-through
 * legal package), BrandRick's Data Sharing Agreement Draft 7 as carried in
 * the Institutional Partnership Agreement Suite, the three review rounds, and
 * the positioning decisions of Sep 8 and 9 2026 (a capacity commitment, not
 * seats; no roster on the self-serve path; verification, not capability, is
 * the distinction). BrandRick and Orelia review before anything is published,
 * and publication is the act of setting `status: "published"`, an effective
 * date, and the digest pin. Nothing here is legal advice.
 *
 * ── COPY RULES ────────────────────────────────────────────────────────────
 * US spelling. No em dashes. "Students", never the other word, so the
 * milestone framework is "the milestone framework" here. Nothing asserts a
 * capability P3 does not have; where P3 lacks something (SOC 2, a penetration
 * test, single sign-on, a service level agreement, enrollment verification)
 * the document says so in the affirmative. tests/legal.test.ts holds all of
 * it.
 */

import {
  ANNUAL_PAID_MONTHS,
  CANCELLATION_NOTICE_DAYS,
  CONVENIENCE_NOTICE_DAYS,
  CURE_PERIOD_DAYS,
  DATA_RESIDENCY,
  EXPORT_RETENTION_MONTHS,
  INVOICE_NET_DAYS,
  KEY_TERMS,
  LEGAL,
  QUOTE_VALID_DAYS,
  RENEWAL_NOTICE_DAYS,
  SUBPROCESSORS,
  SUBPROCESSOR_NOTICE_DAYS,
  SUPPORT_RESPONSE_BUSINESS_DAYS,
  SUPPRESSION_EXPLAINED,
  SUPPRESSION_FLOOR,
  TERM_MONTHS,
  WHAT_A_COMMITMENT_IS,
  WHAT_P3_DOES_NOT_HOLD,
  WHAT_P3_HOLDS,
} from "~/app/lib/pricing";

/* ==========================================================================
   1. The shapes
   ========================================================================== */

export type DocumentId = "enterprise-terms" | "data-sharing-addendum";

/**
 * "draft": readable at its address, clearly marked as not in force, free to
 * change. "published": in force from `effectiveDate`, hash pinned, never
 * edited again.
 */
export type PublicationStatus = "draft" | "published";

export interface Clause {
  /** "10.1". Absent for an unnumbered paragraph inside a section. */
  readonly ref?: string;
  /** A short run-in heading, rendered bold before the text. */
  readonly heading?: string;
  readonly text: string;
  /** Bullet items that follow the paragraph. */
  readonly items?: readonly string[];
}

export interface Section {
  /** "10" in the Terms, "X" in the Addendum. */
  readonly ref: string;
  readonly heading: string;
  readonly clauses: readonly Clause[];
}

export interface LegalDocument {
  readonly id: DocumentId;
  readonly title: string;
  readonly shortTitle: string;
  /** "2026.1", as the acceptance box and the pricing constants say it. */
  readonly version: string;
  /** "2026-1", as the address says it. */
  readonly slug: string;
  /** "/legal/enterprise-terms/2026-1". */
  readonly path: string;
  readonly status: PublicationStatus;
  /** ISO date, or null while the version is a draft. */
  readonly effectiveDate: string | null;
  /** One line for the version history. */
  readonly changeNote: string;
  readonly preamble: readonly string[];
  readonly sections: readonly Section[];
  readonly closing: readonly string[];
}

/** The shape the checkout pane renders. Projected from a document, never typed. */
export interface DocumentClause {
  readonly ref: string;
  readonly heading: string;
  readonly body: readonly string[];
}

/* ==========================================================================
   2. Addresses
   ========================================================================== */

export const DOCUMENT_BASE: Readonly<Record<DocumentId, string>> = {
  "enterprise-terms": "/legal/enterprise-terms",
  "data-sharing-addendum": "/legal/data-sharing-addendum",
};

export const SUBPROCESSORS_PATH = "/legal/subprocessors";

/** "2026.1" becomes "2026-1". The dot is the version, the hyphen is the URL. */
export function versionSlug(version: string): string {
  return version.replace(/\./g, "-");
}

export function documentPath(id: DocumentId, version: string): string {
  return `${DOCUMENT_BASE[id]}/${versionSlug(version)}`;
}

/* ==========================================================================
   3. Decisions the drafts carry, named so they can be found
   ========================================================================== */

/**
 * The dollar floor under the liability cap (Terms clause 15.2). The spec's
 * recommendation to BrandRick (Part 5, B5): a stated floor rather than
 * "applicable insurance limits", because pointing the cap at the policy limit
 * hands a claimant that limit and moves the cap whenever coverage changes.
 * HIS CALL. A draft constant, not a settled number.
 */
export const LIABILITY_FLOOR_USD = 50_000;

/** Hours from confirming a security incident to notifying administrators. */
export const INCIDENT_NOTICE_HOURS = 72;

/** Days P3 takes to answer a verified data subject request. */
export const DATA_REQUEST_RESPONSE_DAYS = 30;

const money = (usd: number): string =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(usd);

const keyTerm = (clause: string): string => {
  const term = KEY_TERMS.find((t) => t.clause === clause);
  if (!term) throw new Error(`KEY_TERMS has no clause ${clause}; the Terms embed it verbatim`);
  return term.text;
};

const holding = (row: { label: string; body: string }): string => `${row.label}. ${row.body}`;

/* ==========================================================================
   4. P3 Enterprise Subscription Terms, version 2026.1 (DRAFT)
   ========================================================================== */

/* 2026.1 is frozen: its version strings are literal, so the LEGAL constants
   (which name the CURRENT version the acceptance box shows) can move on
   without moving a pinned text. */
const TERMS_VERSION_1 = "2026.1";
const DSA_VERSION_1 = "2026.1";
const DSA_PATH_1 = documentPath("data-sharing-addendum", DSA_VERSION_1);

export const TERMS_2026_1: LegalDocument = {
  id: "enterprise-terms",
  title: "P3 Enterprise Subscription Terms",
  shortTitle: "Enterprise Subscription Terms",
  version: TERMS_VERSION_1,
  slug: versionSlug(TERMS_VERSION_1),
  path: documentPath("enterprise-terms", TERMS_VERSION_1),
  status: "published",
  effectiveDate: "2026-09-15",
  changeNote:
    "First version, in force from September 15, 2026. It carries the substance of the Master Services Agreement, under Louisiana law.",
  preamble: [
    `These Enterprise Subscription Terms ("Terms") are a contract between ${LEGAL.entity} (i.e., nonprofit entity: ${LEGAL.nonprofitEntity}), a ${LEGAL.status} incorporated in ${LEGAL.incorporation}, EIN ${LEGAL.ein}, with its principal place of business at ${LEGAL.address} ("P3", "we", "us"), and the institution identified at checkout ("you", "your institution").`,
    "You accept these Terms by ticking the acceptance box at checkout. The person ticking it confirms that they are authorized to enter into this agreement for your institution. If you do not accept these Terms, do not register.",
    `These Terms incorporate the P3 Data Sharing Addendum ${DSA_VERSION_1} at ${DSA_PATH_1} (the "Addendum"), which you accept at the same time and which governs privacy, security and data governance. If these Terms and the Addendum conflict, the Addendum controls on privacy, security and data governance, and these Terms control on everything else.`,
  ],
  sections: [
    {
      ref: "1",
      heading: "What you are buying",
      clauses: [
        {
          ref: "1.1",
          text: "Your subscription gives your institution access to the P3 institutional dashboard (the \"Dashboard\"), which reports on the students who have claimed your institution on the P3 platform, together with the Administrator accounts needed to use it, a join kit (a link, a six character code and a QR code) that students use to claim your institution, CSV and printable exports, and email support.",
        },
        {
          ref: "1.2",
          text: "The Dashboard reports aggregated information about student participation. It does not provide identifiable student level records, and Section 11 and the Addendum explain why.",
        },
        {
          ref: "1.3",
          heading: "What the Dashboard does not do",
          text: "So there is no misunderstanding at purchase:",
          items: [
            WHAT_P3_DOES_NOT_HOLD[1]!.body,
            "P3 holds no cost data about your institution, and no dollar figure of any kind appears in the Dashboard.",
            "P3 compares your students against all P3 students in aggregate. That is a comparison between groups, never a controlled experiment, and the Dashboard does not establish that P3 caused any outcome.",
            "The Dashboard is not real time. Its figures are recomputed on a schedule, and it shows the time they were computed.",
            "P3 offers no public API, no single sign-on, and no connection to a student information system or learning management system.",
            "P3 does not verify that a student who claims your institution attends it. Affiliation is asserted by the student.",
          ],
        },
        {
          ref: "1.4",
          text: "We may improve, revise, replace or retire particular Dashboard features during your Subscription Period, provided the subscription as a whole keeps substantially equivalent functionality and value. Section 18 governs changes to these Terms.",
        },
        {
          /* From the Master Services Agreement, sections 3 and 8 (Sep 12
             2026): work beyond the subscription is sold separately and in
             writing. Without it, a buyer could reasonably read custom work as
             included in a band price. It adds no capability: clause 10.3
             still says what P3 does not offer today, and a statement of work
             is a separate signed deal about its own scope. */
          ref: "1.5",
          heading: "Work beyond the subscription",
          text: "Custom analysis, implementation work, additional support and other services beyond the subscription are not included in it. Where P3 agrees to provide any of them, it does so only under a separate statement of work signed by both of us, which states its own scope and fees.",
        },
      ],
    },
    {
      ref: "2",
      heading: "Some definitions",
      clauses: [
        {
          heading: "Claimed Student",
          text: "A student account on the P3 platform whose holder has claimed your institution, from your join link, your six character code or your printed poster, and has not removed that claim.",
        },
        {
          heading: "Administrator",
          text: "A person you authorize to sign in to the Dashboard. Administrator accounts are free and there is no fixed limit on how many you create. There are three roles: owner, admin and viewer.",
        },
        {
          heading: "Capacity band",
          text: "The size of program you chose at checkout, stated as a number of students. It sets your price. It is not a meter and it is not a limit on how many students may claim you. Section 6 explains it.",
        },
        {
          heading: "Order Summary",
          text: "The screen shown to you at checkout and repeated in your receipt, stating your institution's legal name, your capacity band, your price, your billing frequency, and the start, end and renewal dates of your Subscription Period. The Order Summary is part of these Terms.",
        },
        {
          heading: "Subscription Period",
          text: `For an annual subscription, the ${TERM_MONTHS} month period stated in your Order Summary, and each renewal period after it. For a monthly subscription, each month of service, counted from the start date stated in your Order Summary.`,
        },
        {
          heading: "Addendum",
          text: `The P3 Data Sharing Addendum ${DSA_VERSION_1}, at ${DSA_PATH_1}.`,
        },
      ],
    },
    {
      ref: "3",
      heading: "How a student claims your institution",
      clauses: [
        {
          ref: "3.1",
          text: "A student claims your institution inside the free P3 app, by opening your join link, typing your six character code, or scanning your printed poster. A student can remove the claim at any time.",
        },
        {
          ref: "3.2",
          text: `P3 does not accept a list of your students under these Terms. If your institution needs to supply a roster, do not register online. Write to ${LEGAL.contactEmail} and we will route you to a signed agreement that covers the data on it.`,
        },
        {
          ref: "3.3",
          heading: "What we do not promise",
          text: "Claiming depends on a student's own action. We do not promise that every student affiliated with your institution will claim it, we do not promise that your Claimed Student count will match your enrollment, and we do not verify that a student who claims your institution attends it.",
        },
        {
          ref: "3.4",
          text: "Nothing about your subscription changes what a student can do in the P3 app. Every student feature is free, open to students whether or not their institution subscribes, and unaffected by your payment status.",
        },
      ],
    },
    {
      ref: "4",
      heading: "Fees, billing and taxes",
      clauses: [
        {
          ref: "4.1",
          text: "You pay the fees stated in your Order Summary. Fees are set by your capacity band, not by how many students claim you and not by how much they use the app.",
        },
        {
          ref: "4.2",
          heading: "Billing",
          text: `Annual subscriptions are invoiced or charged in advance for the full Subscription Period. Monthly subscriptions are charged in advance each month. An annual subscription is billed at ${ANNUAL_PAID_MONTHS} months of the monthly price for the same band.`,
        },
        {
          ref: "4.3",
          heading: "Changing your band",
          text: "You may move to a larger band at any time by writing to us. We show you the exact prorated amount before anything is charged, and we charge only that amount. A move to a smaller band takes effect at your next renewal.",
        },
        {
          ref: "4.4",
          heading: "Taxes",
          text: "Fees do not include sales, use or similar taxes. You are responsible for them, except for taxes on P3's income. If you are exempt, send us a valid exemption certificate and we will apply it from the date we receive it.",
        },
        {
          ref: "4.5",
          heading: "No refunds",
          text: "Fees are non-refundable except where Section 16 says otherwise.",
        },
      ],
    },
    {
      ref: "5",
      heading: "Paying by invoice or purchase order",
      clauses: [
        {
          ref: "5.1",
          text: "If your Order Summary says your subscription is invoiced, the following applies:",
          items: [
            `Invoices are due ${INVOICE_NET_DAYS} days from the invoice date unless the Order Summary says otherwise.`,
            "Your subscription starts when we send the invoice. You do not have to wait for the payment to clear.",
            "If you need a purchase order number on the invoice, give it to us before we issue the invoice. We cannot add one afterwards without voiding and reissuing.",
            `We accept ACH, wire and check. Our W-9 is available on request from ${LEGAL.contactEmail} and from the procurement pack on our Security page at /security.`,
            "Undisputed amounts more than 30 days past due may accrue interest at the lesser of 1.5 percent per month or the maximum rate the law allows. We do not charge interest on an amount you have disputed in good faith and in writing.",
            "Late payment does not affect students. If an invoice is overdue we contact your billing contact and your Administrators, and we may eventually restrict Dashboard access. We never restrict a student's access to the P3 app.",
          ],
        },
        {
          ref: "5.2",
          text: `A written quote is valid for ${QUOTE_VALID_DAYS} days, and longer if you ask.`,
        },
      ],
    },
    {
      ref: "6",
      heading: "Your capacity band",
      clauses: [
        { ref: "6.1", text: WHAT_A_COMMITMENT_IS[0]! },
        { ref: "6.2", text: WHAT_A_COMMITMENT_IS[1]! },
        { ref: "6.3", text: WHAT_A_COMMITMENT_IS[2]! },
      ],
    },
    {
      ref: "7",
      heading: "Term, renewal, and how to stop it",
      clauses: [
        { ref: "7.1", heading: "Term and renewal", text: keyTerm("7.1") },
        {
          ref: "7.2",
          heading: "How to stop it",
          text: `To stop the renewal, turn renewal off in your Dashboard under Billing, or email ${LEGAL.contactEmail} from an Administrator address saying you do not want to renew. For an annual subscription, do it at least ${CANCELLATION_NOTICE_DAYS} days before the renewal date. For a monthly subscription, the subscription then ends at the end of the next full month. That is all it takes. We will not ask you to call anyone.`,
        },
        {
          ref: "7.3",
          heading: "Confirmation",
          text: "When you stop renewal we email your Administrators to confirm within one business day. If you do not get that email, assume it did not take effect and contact us.",
        },
        {
          ref: "7.4",
          text: "Stopping renewal does not shorten your Subscription Period. Your Dashboard stays live until the end of the last period you pay for, and Section 16 describes what happens after that.",
        },
      ],
    },
    {
      ref: "8",
      heading: "Your license, and what you may not do",
      clauses: [
        {
          ref: "8.1",
          heading: "License",
          text: "For your Subscription Period, we grant your institution a limited, non-exclusive, non-transferable, revocable right for your Administrators to access and use the Dashboard for your institution's internal purposes, including your own reporting to your board, your accreditors and your funders.",
        },
        {
          ref: "8.2",
          heading: "You may not",
          text: "Do any of the following:",
          items: [
            "resell, sublicense or provide Dashboard access to a third party, other than your own advisors under confidentiality;",
            "attempt to re-identify any individual from aggregated or de-identified output, or combine it with other data to do so;",
            "reverse engineer or attempt to derive the source code or the underlying analytics methods of the platform;",
            "circumvent or test our security controls without our written permission;",
            "share Administrator credentials, including with other people at your institution (create them another Administrator instead; they are free);",
            "use the Dashboard to build a competing product; or",
            "use the Dashboard for any unlawful purpose or in a way that harms a student.",
          ],
        },
        {
          ref: "8.3",
          heading: "Your Administrators",
          text: "You are responsible for what your Administrators do, and for removing them promptly when they leave your institution or change roles.",
        },
      ],
    },
    {
      ref: "9",
      heading: "What you agree to do",
      clauses: [
        {
          ref: "9.1",
          text: "You agree to name at least one Administrator, to give us accurate information and keep your contact and billing details current, to tell us promptly when an Administrator should be removed, to keep your Administrators' sign-in secure, and to tell us promptly at team@pulseofp3.org if you believe an Administrator account has been used by someone who should not have it. You acknowledge that what the Dashboard shows depends on your students choosing to claim your institution and to use the P3 app, which neither of us controls.",
        },
      ],
    },
    {
      ref: "10",
      heading: "What we commit to, and what we do not",
      clauses: [
        { ref: "10.1", heading: "No service level agreement", text: keyTerm("10.1") },
        {
          ref: "10.2",
          heading: "Support",
          text: `We provide email support at ${LEGAL.contactEmail} on business days, United States Central Time. We aim to respond within ${SUPPORT_RESPONSE_BUSINESS_DAYS} business days. That is a target, not a guarantee, and it is not a commitment to resolve anything within any period.`,
        },
        {
          ref: "10.3",
          heading: "What we do not offer today",
          text: "P3 does not offer round the clock support, a dedicated named support contact, a public status page, an API, single sign-on, or a connection to a student information system or learning management system. If any of these is required for your purchase, say so before you register, because it changes what we can sell you.",
        },
      ],
    },
    {
      ref: "11",
      heading: "Data, privacy and security",
      clauses: [
        {
          ref: "11.1",
          heading: "The Addendum governs",
          text: "All privacy, security, confidentiality of student information and data governance obligations are governed by the Addendum, which you accepted with these Terms and which is part of this agreement.",
        },
        {
          ref: "11.2",
          heading: "The short version",
          text: "Students register with P3 directly and consent to P3 directly. P3 is the controller of that data. You receive aggregated reporting, not student records. P3 is not a school official under the Family Educational Rights and Privacy Act and does not hold education records on your behalf.",
        },
        { ref: "11.3", heading: "Small numbers are suppressed", text: keyTerm("11.3") },
        {
          ref: "11.4",
          heading: "What your browser receives",
          text: "No endpoint that returns your analytics accepts an organization identifier from the caller. Your institution is read from your Administrator's server side session, and your browser never receives a student level row.",
        },
      ],
    },
    {
      ref: "12",
      heading: "Intellectual property",
      clauses: [
        {
          ref: "12.1",
          text: "The platform, the Dashboard, our software, our analytics methods, our milestone framework, our branding and everything we create remain ours. Your name, your marks and any information you give us remain yours. Nothing in these Terms transfers ownership of anything to anyone.",
        },
        {
          ref: "12.2",
          text: "If you send us feedback or suggestions, we may use them without owing you anything and without restriction.",
        },
      ],
    },
    {
      ref: "13",
      heading: "Confidentiality",
      clauses: [
        {
          ref: "13.1",
          text: "Each of us will protect the other's non-public information that is marked confidential or that a reasonable person would understand to be confidential, will use it only for this agreement, and will protect it with at least the care we use for our own confidential information. This does not cover information that is public, that the receiving party already had, that it develops independently, or that it lawfully receives from someone else. Either of us may disclose the other's confidential information if legally required, after giving the other reasonable advance notice where we lawfully can. These obligations survive for three years after this agreement ends, and for as long as the law requires in the case of student information.",
        },
      ],
    },
    {
      ref: "14",
      heading: "Warranties and disclaimers",
      clauses: [
        {
          ref: "14.1",
          heading: "What we warrant",
          text: "We warrant that we will provide the subscription in a professional and workmanlike manner, and that we have the right to grant the license in Section 8.",
        },
        {
          ref: "14.2",
          heading: "What we do not warrant",
          text: "EXCEPT AS STATED IN CLAUSE 14.1, THE SUBSCRIPTION IS PROVIDED \"AS IS\". WE DISCLAIM ALL OTHER WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, ACCURACY, AND UNINTERRUPTED OR ERROR-FREE OPERATION.",
        },
        { ref: "14.3", heading: "No outcome is guaranteed", text: keyTerm("14.3") },
      ],
    },
    {
      ref: "15",
      heading: "Limitation of liability",
      clauses: [
        { ref: "15.1", heading: "Liability is capped, including privacy liability", text: keyTerm("15.1") },
        {
          ref: "15.2",
          heading: "The cap",
          text: `Each party's total liability arising out of or relating to this agreement, whatever the theory, will not exceed the greater of the fees you paid us in the twelve months before the event giving rise to the claim, or ${money(LIABILITY_FLOOR_USD)}.`,
        },
        {
          ref: "15.3",
          heading: "Exceptions",
          text: "Clause 15.2 does not limit your obligation to pay fees you owe, or either party's liability for fraud, willful misconduct, or anything that cannot be limited by law.",
        },
        {
          ref: "15.4",
          heading: "Insurance",
          text: "P3 maintains commercial general liability and cyber liability insurance, and provides a certificate of insurance on reasonable request. The coverage types and limits are the ones stated on that certificate.",
        },
      ],
    },
    {
      ref: "16",
      heading: "Ending the agreement",
      clauses: [
        {
          ref: "16.1",
          heading: "Non-renewal",
          text: "Either of us can stop the subscription renewing under clause 7.2.",
        },
        {
          ref: "16.2",
          heading: "For cause",
          text: `Either of us may terminate immediately on written notice if the other materially breaches this agreement and does not cure it within ${CURE_PERIOD_DAYS} days of written notice; acts unlawfully in connection with this agreement, which no cure period can put right; or becomes insolvent, files for bankruptcy, or has a receiver appointed.`,
        },
        { ref: "16.3", heading: "For convenience", text: keyTerm("16.3") },
        {
          ref: "16.4",
          heading: "Refunds on termination",
          text: "If you terminate for our uncured material breach under clause 16.2, we refund the unused portion of prepaid fees, prorated to the termination date. If you terminate for convenience under clause 16.3, any unpaid fees for the current Subscription Period remain due.",
        },
        { ref: "16.5", heading: "What happens to your data if it ends", text: keyTerm("16.5") },
        {
          ref: "16.6",
          heading: "What happens to institutional records",
          text: "The Addendum governs what happens to data. Because P3 accepts no roster under these Terms, there is no institutional record to return or delete.",
        },
        {
          ref: "16.7",
          heading: "Suspension",
          text: "We may suspend Dashboard access, on notice, if your use threatens the security or integrity of the platform or is unlawful, and we restore it as soon as the cause is resolved. We never suspend a student's access to the P3 app because of anything in this agreement.",
        },
      ],
    },
    {
      ref: "17",
      heading: "Using each other's names",
      clauses: [
        { ref: "17.1", heading: "What we may do, and what needs your approval", text: keyTerm("17.1") },
        {
          ref: "17.2",
          heading: "What you may do",
          text: "You may identify P3 as your partner and use our name and logo to describe the relationship, following our brand guidelines.",
        },
        {
          ref: "17.3",
          heading: "Nothing identifiable",
          text: "Neither of us will publish anything that identifies an individual student without that student's own consent.",
        },
      ],
    },
    {
      ref: "18",
      heading: "Changes to these Terms",
      clauses: [
        {
          ref: "18.1",
          heading: "Material changes",
          text: `If we change these Terms or the Addendum in a way that materially reduces your rights or materially increases your obligations, the change does not apply to your current Subscription Period. We email your Administrators at least ${RENEWAL_NOTICE_DAYS[1]} days before your renewal date with the new version and a plain summary of what changed. The new version applies from the start of your next Subscription Period, and you accept it by re-accepting it. If you do not want the new version, do not renew.`,
        },
        {
          ref: "18.2",
          heading: "Other changes",
          text: "We may make non-material changes, such as corrections, clarifications, formatting and contact details, by publishing a new version with a new version number and effective date, and we note the change in the version history.",
        },
        {
          ref: "18.3",
          heading: "Version history",
          text: "Every published version of these Terms and of the Addendum stays at its own permanent address and is never edited after publication. We keep a record of which version you accepted and when, and your receipt links to it.",
        },
      ],
    },
    {
      ref: "19",
      heading: "Governing law, and public institutions",
      clauses: [
        { ref: "19.1", heading: `${LEGAL.governingLaw} law`, text: keyTerm("19.1") },
        {
          ref: "19.2",
          heading: "Venue and conflicts",
          text: `The state and federal courts located in ${LEGAL.venue} have exclusive jurisdiction over any dispute arising out of this agreement, and each of us consents to that jurisdiction. These Terms are governed by ${LEGAL.governingLaw} law without regard to its conflict of law rules. The United Nations Convention on Contracts for the International Sale of Goods does not apply.`,
        },
        {
          ref: "19.3",
          heading: "Public institutions",
          text: "If you are a public institution, an agency or an instrumentality of a state, and the law of your state prohibits you from agreeing to clause 19.1 or 19.2, those clauses do not apply to you. In that case this agreement is governed by the law of the state that created you, without regard to its conflict of law rules, and venue lies where that state's law requires.",
        },
        {
          ref: "19.4",
          heading: "No jury, no class",
          text: "Each of us waives trial by jury. Each of us brings claims only in its own capacity and not as a class representative.",
        },
      ],
    },
    {
      ref: "20",
      heading: "General",
      clauses: [
        {
          ref: "20.1",
          heading: "Notices",
          text: `Notices to P3 go to ${LEGAL.contactEmail} and to ${LEGAL.entity}, ${LEGAL.address}. Notices to you go to the Administrator and billing email addresses in your account. Email notice is effective when sent, unless it bounces.`,
        },
        {
          ref: "20.2",
          heading: "Assignment",
          text: "Neither of us may assign this agreement without the other's written consent, except that either may assign it in full to a successor in a merger, reorganization or sale of substantially all assets, on notice.",
        },
        {
          ref: "20.3",
          heading: "Force majeure",
          text: "Neither of us is liable for a delay or failure caused by something outside our reasonable control, including natural disaster, cyberattack, government action, labor disruption, utility or internet failure, or a public health emergency. This does not excuse payment.",
        },
        {
          ref: "20.4",
          heading: "Independent parties",
          text: "We are independent contractors. This agreement creates no partnership, joint venture, agency or employment relationship.",
        },
        {
          ref: "20.5",
          heading: "No third party beneficiaries",
          text: "Nobody other than your institution and P3 has rights under this agreement.",
        },
        {
          ref: "20.6",
          heading: "Severability",
          text: "If a provision is unenforceable, the rest stays in force and the unenforceable provision is modified to the minimum extent needed to make it enforceable.",
        },
        {
          ref: "20.7",
          heading: "No waiver",
          text: "Not enforcing a right once does not waive it.",
        },
        {
          ref: "20.8",
          heading: "Entire agreement",
          text: "These Terms, your Order Summary, the Addendum and any statement of work we have both signed are the entire agreement between us about the subscription and replace anything said or written before. Any purchase order, vendor portal terms, click-through supplier terms, or other document you issue is for your internal administration only, and any additional or conflicting terms in it have no effect, unless P3 signs a document that expressly amends this agreement.",
        },
        {
          ref: "20.9",
          heading: "Survival",
          text: "Section 4 (as to amounts owed), clause 8.2, and Sections 11, 12, 13, 14, 15, 19 and 20 survive termination, as do clauses 16.3 to 16.6.",
        },
        {
          ref: "20.10",
          heading: "Order of precedence",
          text: "If these documents conflict, the order is: a signed amendment; the Addendum, on privacy, security and data governance; these Terms; a signed statement of work, as to the services it describes; the Order Summary.",
        },
      ],
    },
  ],
  closing: ["End of the Enterprise Subscription Terms."],
};

/* ==========================================================================
   5. P3 Data Sharing Addendum, version 2026.1 (DRAFT)

   BrandRick's Draft 7 (fifteen sections, reviewed across three rounds) with
   the seven changes the click-through spec calls for: retitled as an
   addendum with no signature page and no party block; the SOC 2 and NIST
   representation replaced by an affirmative negative disclosure; residency
   rewritten to distinguish storage from transient edge processing; the
   subprocessor schedule rebuilt from the live list; the independent sixty
   day termination right removed so the exhibit grants nothing the parent
   does not; the under-18 section corrected (COPPA reaches under 13); and
   the audit right rewritten around documents P3 can actually hand over.

   The section numerals are the ones the checkout pane has cited since it
   shipped (I, II, III, V, VII, X, XII), so the pane's excerpt reads as an
   excerpt of this document and not of a different one.
   ========================================================================== */

export const subprocessorItems = (): readonly string[] =>
  SUBPROCESSORS.map((s) => `${s.name}: ${s.purpose} Region: ${s.region}.`);

/**
 * SECTION X'S ITEMS ARE FROZEN PER VERSION, AND THIS IS WHY (Sep 19 2026).
 *
 * A published version is never edited: `plainText` walks its clauses' items,
 * `digest()` hashes that text, and PUBLISHED_DIGESTS pins the hash. Section X
 * rendered `subprocessorItems()`, a LIVE map over SUBPROCESSORS, so editing
 * one subprocessor's purpose or region in pricing.ts silently rewrote the
 * text of two contracts already in force and broke both pins. Nobody had
 * noticed only because no purpose had changed since publication.
 *
 * So each published version carries the literal text it was published with,
 * and `subprocessorItems()` is for the NEXT, unpublished version only. The
 * frozen copy below is the mapper's own output on the day of the freeze, and
 * a test asserts the two still agree, which is what proves the freeze faithful
 * rather than merely fixed.
 */
export const SUBPROCESSOR_ITEMS_2026_1: readonly string[] = [
  "Amazon Web Services, Inc.: Core platform infrastructure and the application database. Region: United States.",
  "Cloudflare, Inc.: Edge compute and databases for the enterprise gateway, Pulse AI, Conversations and the web app. Network security and content delivery. Region: United States, with transient edge processing worldwide.",
  "Anthropic, PBC: AI features: the Pulse assistant, Discover, CV review, and automated moderation of mentor and student messages. Region: United States.",
  "Algolia, Inc.: Search indexes for mentors, students, opportunities, posts and questions. This is the index the institutional dashboard reads. Region: United States.",
  "Google LLC (Firebase): Mobile analytics, crash reporting and deep links. Region: United States.",
  "Amplitude, Inc.: Product analytics. Region: United States.",
  "Retool, Inc.: Internal administration console used by P3 staff. Region: United States.",
  "Twilio Inc. (SendGrid): Transactional email. Region: United States.",
  "Resend: Administrator sign-in codes and safeguarding alert delivery. Region: United States.",
  "Intercom, Inc.: Customer messaging and support. Region: United States.",
  "Functional Software, Inc. (Sentry): Error and crash monitoring, including session replay. Region: United States.",
  "Stripe, Inc.: Payments, invoicing and quotes. Stripe never receives student data. Region: United States.",
  "3Advance, LLC: Engineering delivery partner for the P3 platform. Region: United States.",
];

export const DSA_2026_1: LegalDocument = {
  id: "data-sharing-addendum",
  title: "P3 Data Sharing Addendum",
  shortTitle: "Data Sharing Addendum",
  version: DSA_VERSION_1,
  slug: versionSlug(DSA_VERSION_1),
  path: DSA_PATH_1,
  status: "published",
  effectiveDate: "2026-09-15",
  changeNote:
    "First version as an online addendum, in force from September 15, 2026. Converted from the Data Sharing Agreement reviewed with counsel in May 2026.",
  preamble: [
    `This P3 Data Sharing Addendum ("Addendum") forms part of the P3 Enterprise Subscription Terms ${TERMS_VERSION_1} ("Terms") between ${LEGAL.entity} (i.e., nonprofit entity: ${LEGAL.nonprofitEntity}) ("P3") and the institution identified at checkout ("you", "your institution"). It takes effect on the date you accept the Terms, and it is accepted electronically together with them. It has no separate signature page.`,
    "If this Addendum and the Terms conflict, this Addendum controls on privacy, security and data governance, and the Terms control on everything else.",
  ],
  sections: [
    {
      ref: "I",
      heading: "What this Addendum covers",
      clauses: [
        {
          text: `This Addendum governs personal data about your students that P3 processes in connection with your subscription. It forms part of the Enterprise Subscription Terms ${TERMS_VERSION_1} and is entered into by ${LEGAL.entity}, a ${LEGAL.status} incorporated in ${LEGAL.incorporation}.`,
        },
        {
          text: "P3 does not claim school official status under the Family Educational Rights and Privacy Act, and this Addendum is not drafted on that basis.",
        },
        {
          text: "P3 runs a free mobile and web platform on which students register directly, consent to P3's own Terms and Privacy Policy directly, and use mentorship, opportunity, career pathway and milestone features. Your institution receives aggregated reporting about the students who have claimed it. Your institution does not provide education records to P3 under this Addendum, and P3 does not process any on your behalf.",
        },
        {
          text: "This Addendum applies to data students submit directly to P3, to the affiliation a student asserts by claiming your institution, and to the aggregated reporting P3 generates for your institution from them.",
        },
        {
          text: "The analytics available through the Dashboard describe student participation and engagement on the P3 platform, as students reported it. P3 holds no registrar or employer record and does not verify anything a student reports against your systems or anyone else's.",
        },
      ],
    },
    {
      ref: "II",
      heading: "What P3 holds",
      clauses: WHAT_P3_HOLDS.map((row) => ({ text: holding(row) })),
    },
    {
      ref: "III",
      heading: "What P3 does not hold, and will not accept",
      clauses: [
        ...WHAT_P3_DOES_NOT_HOLD.map((row) => ({ text: holding(row) })),
        {
          text: "P3 does not accept a list of your students. Students claim your institution themselves, from a join link, a six character code or a printed poster, and a student can remove that claim at any time.",
        },
        {
          text: "If your institution needs P3 to receive a roster, that requires a separate signed agreement naming the data elements, the transfer method, the retention period and the deletion trigger. It cannot be accepted online, and nothing in this Addendum authorizes it.",
        },
      ],
    },
    {
      ref: "IV",
      heading: "Who controls the data",
      clauses: [
        {
          text: "P3 is the controller of the personal data it collects directly from students through the P3 platform, including the profile a student completes, their mentorship activity, their engagement with opportunities and career pathways, their questions, their milestone check-ins and the notes they attach to them, and the institution they claim. Students hold a direct relationship with P3 under P3's own Terms and Privacy Policy.",
        },
        {
          text: "Your institution is an independent controller of any information it holds in its own systems. This Addendum does not make P3 a processor of your institution's records and does not establish a controller to processor relationship for platform data, because none of that data comes from you.",
        },
        {
          text: "Where P3 generates aggregated reporting for your institution, P3 acts as an independent controller for the purpose of creating it, and the output is governed by Sections V and VI.",
        },
        {
          text: `Requests from students to access, correct, delete, port or restrict their personal data are directed to P3 and handled under P3's Privacy Policy at ${LEGAL.privacyUrl}. P3 responds to a verified request within ${DATA_REQUEST_RESPONSE_DAYS} days unless the law requires sooner. Your institution will not act on a student's platform data, and cannot, because it never receives it.`,
        },
        {
          text: "P3 is not a school official under the Family Educational Rights and Privacy Act and does not maintain education records on your behalf. Data a student submits to P3, including the affiliation the student asserts, is not an education record of your institution in P3's hands. Because P3 accepts no roster under this Addendum, no obligation under that Act attaches to anything your institution supplies.",
        },
      ],
    },
    {
      ref: "V",
      heading: `No figure below ${SUPPRESSION_FLOOR} students`,
      clauses: [
        ...SUPPRESSION_EXPLAINED.map((text) => ({ text })),
        {
          text: `The floor is ${SUPPRESSION_FLOOR} individuals. It is a commitment in this Addendum and not a setting, and changing it is an amendment to this Addendum. Where a figure is withheld, the Dashboard says that it is withheld and why.`,
        },
      ],
    },
    {
      ref: "VI",
      heading: "What the Dashboard reports",
      clauses: [
        {
          text: "P3 provides your institution with the Dashboard and its exports. At the effective date of this Addendum the Dashboard reports, for the students who have claimed your institution: how many have claimed it and how that number has grown; how many have at least one active mentorship, and how many have asked for a mentor and are waiting; what students say they want help with, their education goal, their industry interest and their meeting rhythm; their activity on the platform, such as opportunities opened and applied to and questions asked; and the milestones they have completed and the outcomes they have reported, which P3 labels as student reported and unverified.",
        },
        {
          text: "Every figure is a count of the students behind it, and every one is subject to Section V. P3 does not report on a student individually and does not report dollar figures. Your cohort is compared only against all P3 students in aggregate, never against a named institution or a third party benchmark, and no figure is presented as an effect P3 caused.",
        },
        {
          text: "P3 may improve, revise, replace or retire particular figures, frameworks and features, including its milestone framework, provided the Dashboard as a whole keeps substantially equivalent functionality and value. P3 will not add a figure that represents fewer individuals than Section V allows.",
        },
        {
          text: "P3 identifies your institution's cohort only from claims students make themselves, from your join link, your six character code or your printed poster. P3 does not verify enrollment.",
        },
        {
          text: "P3 does not sell personal data. P3 may use aggregated or de-identified information that cannot reasonably identify a student or your institution for platform analytics and improvement, grant and funder reporting, research and impact studies, and publications. Anything that names your institution beyond its name and logo needs your prior written approval under Section 17 of the Terms.",
        },
      ],
    },
    {
      ref: "VII",
      heading: "Where the data is processed",
      clauses: [
        { text: DATA_RESIDENCY },
        {
          text: "P3 will tell your Administrators before it moves storage of personal data governed by this Addendum outside the United States, and will treat that as a material change under Section 18 of the Terms.",
        },
      ],
    },
    {
      ref: "VIII",
      heading: "Security safeguards",
      clauses: [
        {
          text: "P3 maintains administrative, technical and physical safeguards designed to protect the confidentiality, integrity and availability of personal data. P3 does not hold a SOC 2 Type I or Type II report, has not completed a NIST SP 800-171 assessment, and does not represent that it has. Where this Addendum names a safeguard, that safeguard is a present commitment and not an aspiration.",
        },
        {
          text: "The safeguards include:",
          items: [
            "Encryption of data in transit using TLS 1.2 or higher.",
            "Encryption of data at rest on each store P3 uses, as provided by the subprocessors named in Section X.",
            `Suppression of any reported figure representing fewer than ${SUPPRESSION_FLOOR} individuals, applied on P3's servers before the figure is transmitted to your institution.`,
            "No endpoint that returns your institution's analytics accepts an organization identifier from the caller. The institution is read from the Administrator's server side session.",
            "Administrator sign-in by single-use code sent to an address on your institution's verified domain, with server side sessions that P3 revokes when an Administrator is removed.",
            "Server side role checks, re-read from the membership record on every request, for the owner, admin and viewer roles.",
            "An append-only audit log of sign-ins, invitations, role changes, metrics reads and exports, which your institution can export.",
            "Security monitoring and error logging, and documented incident response and disaster recovery planning.",
          ],
        },
        {
          text: "On request and at no charge, P3 completes your institution's standard security questionnaire, including the Higher Education Community Vendor Assessment Toolkit, and supports an assessment of the institutional surface, which is one Cloudflare Worker and one web application.",
        },
        {
          text: "P3 maintains practices designed to align with applicable United States state privacy laws, including the California Consumer Privacy Act as amended by the California Privacy Rights Act, the Virginia Consumer Data Protection Act, the Colorado Privacy Act and the Texas Data Privacy and Security Act, as they apply to P3.",
        },
      ],
    },
    {
      ref: "IX",
      heading: "Security incidents",
      clauses: [
        {
          text: `If P3 confirms unauthorized acquisition, disclosure or compromise of personal data governed by this Addendum, P3 notifies your Administrators within ${INCIDENT_NOTICE_HOURS} hours of confirming it.`,
        },
        {
          text: "The notice includes, where reasonably available: the nature and scope of the incident; the categories of information affected; the containment and remediation measures taken; the mitigation still under way; and any protective steps P3 recommends.",
        },
        {
          text: "P3 cooperates in good faith with your institution on incident response and on any notification the law requires of either party.",
        },
      ],
    },
    {
      ref: "X",
      heading: "Subprocessors",
      clauses: [
        {
          text: `P3 uses the subprocessors named below. P3 gives you ${SUBPROCESSOR_NOTICE_DAYS} days written notice before a new subprocessor starts processing personal data governed by this Addendum.`,
          items: SUBPROCESSOR_ITEMS_2026_1,
        },
        {
          text: `The current list, with a dated change log, is published at ${SUBPROCESSORS_PATH}. If you object to a new subprocessor on documented data protection grounds within the notice period, we discuss it in good faith, and if it cannot be resolved you may terminate under clause 16.3 of the Terms.`,
        },
        {
          text: "Stripe processes your institution's billing contact and payment details and never receives student data. Every subprocessor named here, including P3's engineering delivery partner, is bound by confidentiality and data protection obligations consistent with this Addendum.",
        },
      ],
    },
    {
      ref: "XI",
      heading: "Students under 18",
      clauses: [
        {
          text: "P3's services are not directed to and are not available to children under 13. P3 may provide services to students aged 13 to 17. The Children's Online Privacy Protection Act applies to children under 13, and P3 does not knowingly collect personal information from them.",
        },
        {
          text: "For students aged 13 to 17, P3 screens age at registration, limits collection to what is reasonably necessary to provide mentorship, opportunity, career pathway and milestone features, and applies further restrictions: P3's AI features and direct messaging between a student and a mentor are available only to users aged 18 and over. Figures about AI features in the Dashboard therefore describe students aged 18 and over only, and the Dashboard says so.",
        },
        {
          text: "P3 maintains practices designed to align with applicable state minor privacy and age appropriate design requirements as those laws take effect.",
        },
      ],
    },
    {
      ref: "XII",
      heading: "When the agreement ends",
      clauses: [
        {
          text: `Administrator access to live reporting ends. Your dashboard stays available in read-only export mode for ${EXPORT_RETENTION_MONTHS} months so you can retrieve what you need.`,
        },
        {
          text: "Your students' access to the P3 app is never affected by the end of this agreement. Students hold their own relationship with P3.",
        },
        {
          text: "Because P3 accepts no roster under this Addendum, there is no institutional record to return or delete. Student data stays governed by P3's Privacy Policy and the student's own relationship with P3. Aggregated and de-identified reporting that cannot reasonably identify a student or your institution may be retained. P3 keeps its own record of your acceptance of the Terms and this Addendum for as long as the law allows a claim on them.",
        },
      ],
    },
    {
      ref: "XIII",
      heading: "Vendor review, insurance and liability",
      clauses: [
        {
          text: "Your institution may request reasonable documentation of P3's security and privacy practices. P3 provides, at no charge: its completed response to your security questionnaire, including the Higher Education Community Vendor Assessment Toolkit; its current subprocessor list; a summary of its incident response practice; and a certificate of insurance. P3 does not hold a SOC 2 report or a third party penetration test report and will not represent that it does. If your institution requires either, we discuss scope and cost separately.",
        },
        {
          text: "On-site audits are not permitted unless required by law or agreed in writing. P3 cooperates reasonably with your institution's standard vendor review and security assessment process.",
        },
        {
          text: "P3 maintains commercial general liability and cyber liability insurance and provides a certificate of insurance on reasonable request. The coverage types and limits are the ones stated on that certificate.",
        },
        {
          text: "Liability under this Addendum is governed by Section 15 of the Terms. Liability connected with privacy, confidentiality, security or data protection sits inside the cap in clause 15.2 of the Terms and is not unlimited or uncapped.",
        },
      ],
    },
    {
      ref: "XIV",
      heading: "Term, termination and governing law",
      clauses: [
        {
          text: `This Addendum takes effect when you accept the Terms and remains in effect for as long as the Terms remain in effect, including through any renewal period. It terminates automatically and at the same moment as the Terms. Neither party may terminate this Addendum separately or on notice, and this Addendum grants no right to terminate that the Terms do not grant. Termination rights, including the right of either party to terminate for convenience on ${CONVENIENCE_NOTICE_DAYS} days written notice, are set out in Section 16 of the Terms and apply to this Addendum through them.`,
        },
        {
          text: "This Addendum is governed by the law and subject to the venue stated in Section 19 of the Terms, including the public institution provision in clause 19.3.",
        },
        {
          text: "Sections V, VII, XII and XIII of this Addendum survive termination.",
        },
      ],
    },
    {
      ref: "XV",
      heading: "Entire agreement",
      clauses: [
        {
          text: "This Addendum, together with the Terms and your Order Summary, is the entire agreement between P3 and your institution about the personal data P3 processes in connection with your subscription, and replaces any prior discussion or representation about it. It is accepted electronically together with the Terms and has no separate signature page.",
        },
        {
          text: "Every published version of this Addendum stays at its own permanent address and is never edited after publication. Material changes are made under Section 18 of the Terms, by a new version accepted at renewal.",
        },
      ],
    },
  ],
  closing: ["End of the Data Sharing Addendum."],
};

/* ==========================================================================
   6. The registry
   ========================================================================== */

/* ==========================================================================
   6. Version 2026.2 of both documents (Sep 17 2026): the roster of who has joined

   Thomas's pivot of Sep 17 2026. An institution's own administrators may see
   WHO has joined it, by first name and last initial, when, and whether each
   student has a mentor, together with the mentors who name the institution.
   What a student DOES in P3 stays a figure across ten or more students, the
   floor is unchanged, and P3 still claims no school official status. The
   student consent moved the same day, on the join screen and in P3's own
   Terms, so the roster exists only for institutions registered from that
   date; the ones already on P3 keep counts only (lib/roster.ts).

   Each 2026.2 document is 2026.1 with the clauses below replaced, and the
   cross references pointed at 2026.2. Everything not named here is the
   2026.1 text, word for word.
   ========================================================================== */

/** The day rosters began, as the documents and the dashboard say it. */
export const ROSTER_FROM_DATE = "September 17, 2026";
export const ROSTER_FROM_ISO = "2026-09-17";

interface ClauseRevision {
  readonly section: string;
  /** A numbered clause, "1.2". */
  readonly ref?: string;
  /** An unnumbered clause, by its 0-based place in the section. */
  readonly index?: number;
  /** The clause's new text. Omit to keep the base text. */
  readonly text?: string;
  /** The clause's new list (Sep 19 2026). A safeguard, a subprocessor or any
   *  other item lives in `items`, so a version that corrects one had no way to
   *  say so and would have had to restate the clause it sits under. */
  readonly items?: readonly string[];
}

interface Revision {
  readonly version: string;
  readonly effectiveDate: string;
  readonly changeNote: string;
  readonly clauses: readonly ClauseRevision[];
  /** Applied to every remaining string, for the cross references. */
  readonly substitutions: readonly (readonly [string, string])[];
}

function revise(base: LegalDocument, revision: Revision): LegalDocument {
  const pending = new Set(revision.clauses.map((c) => `${c.section}/${c.ref ?? c.index}`));
  const swap = (text: string): string =>
    revision.substitutions.reduce((acc, [from, to]) => acc.split(from).join(to), text);
  const sections = base.sections.map((section) => ({
    ...section,
    heading: swap(section.heading),
    clauses: section.clauses.map((clause, index) => {
      const hit = revision.clauses.find(
        (c) => c.section === section.ref && (c.ref !== undefined ? c.ref === clause.ref : c.index === index),
      );
      if (hit) pending.delete(`${hit.section}/${hit.ref ?? hit.index}`);
      const items = hit?.items ?? clause.items?.map(swap);
      return {
        ...clause,
        ...(clause.heading !== undefined ? { heading: swap(clause.heading) } : {}),
        text: hit?.text ?? swap(clause.text),
        ...(items ? { items } : {}),
      };
    }),
  }));
  if (pending.size > 0) {
    throw new Error(`legal.ts: revision ${revision.version} names clauses that do not exist: ${[...pending].join(", ")}`);
  }
  return {
    ...base,
    version: revision.version,
    slug: versionSlug(revision.version),
    path: documentPath(base.id, revision.version),
    status: "published",
    effectiveDate: revision.effectiveDate,
    changeNote: revision.changeNote,
    preamble: base.preamble.map(swap),
    sections,
    closing: base.closing.map(swap),
  };
}

/**
 * The cross reference swaps a revision applies to every string it inherits:
 * the address form and the spoken form of the version.
 *
 * A helper, not a constant, since Sep 19 2026: the constant was written for
 * 2026.1 to 2026.2, and a 2026.3 revision that reused it would leave every
 * "2026.2" pointing at the version it replaced, silently.
 */
function versionSubstitutions(from: string, to: string): readonly (readonly [string, string])[] {
  return [
    [`/${from.replace(".", "-")}`, `/${to.replace(".", "-")}`],
    [from, to],
  ];
}

const VERSION_SUBSTITUTIONS = versionSubstitutions("2026.1", "2026.2");

export const TERMS_2026_2: LegalDocument = revise(TERMS_2026_1, {
  version: "2026.2",
  effectiveDate: ROSTER_FROM_ISO,
  changeNote:
    `Second version, in force from ${ROSTER_FROM_DATE}. Adds the roster of who has joined, by first name and last initial, for institutions registered from that date; the consent students give on the join screen changed the same day. Everything else is unchanged from 2026.1.`,
  substitutions: VERSION_SUBSTITUTIONS,
  clauses: [
    {
      section: "1",
      ref: "1.2",
      text: `The Dashboard reports aggregated information about student participation and, for an institution registered from ${ROSTER_FROM_DATE}, a roster of who has joined: each student by first name and last initial, when they joined, and whether they have a mentor, together with the mentors who name your institution. It does not provide any other identifiable student level record, and Section 11 and the Addendum explain why.`,
    },
    {
      section: "11",
      ref: "11.2",
      text: "Students register with P3 directly and consent to P3 directly, and the join screen tells them what your institution sees. P3 is the controller of that data. You receive aggregated reporting and the roster described in clause 1.2: a first name and last initial, a join date and whether a student has a mentor, and nothing else about a student as one person. P3 is not a school official under the Family Educational Rights and Privacy Act and does not hold education records on your behalf.",
    },
    {
      section: "11",
      ref: "11.4",
      text: "No endpoint that returns your analytics accepts an organization identifier from the caller. Your institution is read from your Administrator's server side session. The roster is the only student level list your browser receives; it is shown to Owner and Admin accounts only, and it carries no email address, phone number, message or free text.",
    },
  ],
});

export const DSA_2026_2: LegalDocument = revise(DSA_2026_1, {
  version: "2026.2",
  effectiveDate: ROSTER_FROM_ISO,
  changeNote:
    `Second version, in force from ${ROSTER_FROM_DATE}. Section VI adds the roster of who has joined, by first name and last initial, for institutions registered from that date, and Sections I, IV and V say how it sits beside the figures. The floor, the school official position and everything else are unchanged from 2026.1.`,
  substitutions: VERSION_SUBSTITUTIONS,
  clauses: [
    {
      section: "I",
      index: 2,
      text: "P3 runs a free mobile and web platform on which students register directly, consent to P3's own Terms and Privacy Policy directly, and use mentorship, opportunity, career pathway and milestone features. Your institution receives aggregated reporting about the students who have claimed it, and the roster of who has joined that Section VI describes. Your institution does not provide education records to P3 under this Addendum, and P3 does not process any on your behalf.",
    },
    {
      section: "IV",
      index: 3,
      text: `Requests from students to access, correct, delete, port or restrict their personal data are directed to P3 and handled under P3's Privacy Policy at ${LEGAL.privacyUrl}. P3 responds to a verified request within ${DATA_REQUEST_RESPONSE_DAYS} days unless the law requires sooner. Beyond the roster in Section VI, your institution will not act on a student's platform data, and cannot, because it never receives it.`,
    },
    {
      section: "V",
      index: SUPPRESSION_EXPLAINED.length,
      text: `The floor is ${SUPPRESSION_FLOOR} individuals. It is a commitment in this Addendum and not a setting, and changing it is an amendment to this Addendum. Where a figure is withheld, the Dashboard says that it is withheld and why. The roster in Section VI is a list of people and not a figure: it is the one place a student appears as one person, and it carries only what Section VI states.`,
    },
    {
      section: "VI",
      index: 0,
      text: `P3 provides your institution with the Dashboard and its exports. At the effective date of this Addendum the Dashboard reports, for the students who have claimed your institution: how many have claimed it and how that number has grown; how many have at least one active mentorship, and how many have asked for a mentor and are waiting; what students say they want help with, their education goal, their industry interest and their meeting rhythm; their activity on the platform, such as opportunities opened and applied to and questions asked; and the milestones they have completed and the outcomes they have reported, which P3 labels as student reported and unverified. For an institution registered from ${ROSTER_FROM_DATE}, the Dashboard also shows a roster of who has joined: each student by first name and last initial, the date they joined, and whether they have a mentor, together with the mentors who name your institution on their profile, by first name and last initial, their title, and whether P3 has approved them.`,
    },
    {
      section: "VI",
      index: 1,
      text: "Every figure is a count of the students behind it, and every one is subject to Section V. Beyond the roster, P3 does not report on a student individually, and it does not report dollar figures. The roster carries no email address, phone number, message, question or other free text; it is shown to Owner and Admin accounts only, and it is not exported. Your cohort is compared only against all P3 students in aggregate, never against a named institution or a third party benchmark, and no figure is presented as an effect P3 caused.",
    },
    {
      section: "VI",
      index: 3,
      text: "P3 identifies your institution's cohort only from claims students make themselves, from your join link, your six character code or your printed poster, or by naming your institution on their P3 profile. P3 does not verify enrollment.",
    },
  ],
});

/* ==========================================================================
   7. Version 2026.3 of the Addendum: a correction, IN DRAFT (Sep 19 2026)

   WHAT WAS WRONG. Section VIII's safeguard list said Administrator sign-in
   used "an address on your institution's verified domain". P3 verifies no
   domain: no DNS record is read anywhere and nothing proves ownership. What
   the Worker actually does (routes/session.ts) is refuse an INVITATION
   unless the institution HOLDS that email domain, and send a single-use code
   only to an Administrator's own address. The sentence was a security
   representation, it was live in BOTH published versions (2026.2 revises
   other sections and inherits this one), and it is the first thing a
   university security review would test.

   AMENDED Sep 19 2026, BEFORE COUNSEL READ IT. The first draft of this
   correction said an institution holds a domain only by an active
   Administrator already using it, which was true of the code on the morning
   it was written and false by that evening: migration 0013 and
   routes/operator.ts added the second way, a domain P3 adds at the
   institution's request, which is the mechanism /security had been
   promising with nothing behind it. The safeguard names both, and names the
   absence of verification on its own line so no reader has to infer it.

   WHY NOW. P3's own organization is still the only tenant, so this corrects
   itself before a customer ever accepted it, which is the cheapest moment a
   contract error can be fixed.

   WHY A DRAFT. Counsel has to read it. `revise()` returns a published
   document, and appending it to DOCUMENTS would make it currentVersion(),
   which checkout, Billing and the acceptance record all read: every new buyer
   would accept an unreviewed version the moment this deploys. So it carries
   status "draft" with no effective date, it sits at the TAIL of DOCUMENTS,
   and it has no digest pin. Publication is a separate commit: flip the
   status, set the date, move it to the head of its group, record the digest.
   ========================================================================== */

export const DSA_2026_3: LegalDocument = {
  ...revise(DSA_2026_2, {
    version: "2026.3",
    // Replaced below: a draft has no effective date.
    effectiveDate: "",
    changeNote:
      "Third version, a correction, in draft for counsel. Section VIII's safeguard list said Administrator sign-in used an address on the institution's verified domain. P3 performs no domain verification. The safeguard now says what P3 does: a new Administrator can be invited only at an email domain your institution holds with P3, which is one an active Administrator already uses or one P3 has added at your institution's request and recorded in your audit log, and a single-use code is sent only to an Administrator's own address. The audit log entry in the same list names those additions. No other section changes.",
    substitutions: versionSubstitutions("2026.2", "2026.3"),
    clauses: [
      {
        section: "VIII",
        index: 1,
        items: [
          "Encryption of data in transit using TLS 1.2 or higher.",
          "Encryption of data at rest on each store P3 uses, as provided by the subprocessors named in Section X.",
          `Suppression of any reported figure representing fewer than ${SUPPRESSION_FLOOR} individuals, applied on P3's servers before the figure is transmitted to your institution.`,
          "No endpoint that returns your institution's analytics accepts an organization identifier from the caller. The institution is read from the Administrator's server side session.",
          // The corrected safeguard. It states the invitation rule and the
          // sign-in rule separately, because they are two different controls
          // and only the first one is a domain rule. The non-verification
          // sentence is its own, because it is the representation that was
          // wrong and a reader should not have to infer it from the others.
          "A new Administrator can be invited only at an email domain your institution holds with P3: one an active Administrator of your institution already uses, or one P3 has added at your institution's request, recorded in your audit log. P3 does not verify domain ownership, by DNS record or otherwise. Administrator sign-in is by single-use code sent only to an Administrator's own address, with server side sessions that P3 revokes when an Administrator is removed.",
          "Server side role checks, re-read from the membership record on every request, for the owner, admin and viewer roles.",
          "An append-only audit log of sign-ins, invitations, role changes, metrics reads, exports, and the email domains P3 adds to or removes from your institution's account, which your institution can export.",
          "Security monitoring and error logging, and documented incident response and disaster recovery planning.",
        ],
      },
    ],
  }),
  status: "draft",
  effectiveDate: null,
};

/** Every version of every document. Newest first within a document. */
export const DOCUMENTS: readonly LegalDocument[] = [
  TERMS_2026_2,
  TERMS_2026_1,
  DSA_2026_2,
  DSA_2026_1,
  // At the tail, and only while it is a draft: documentsOf() returns array
  // order, so currentVersion() must keep answering 2026.2 until counsel is
  // done. Publication moves this line to the head of its group.
  DSA_2026_3,
];

/**
 * Digest pins for PUBLISHED versions, keyed `${id}/${slug}`. Publishing a
 * version means: set its status to "published" and its effective date, run
 * `digest()` once, and record the hex here. From then on a test fails if the
 * text moves. A draft has no entry.
 */
export const PUBLISHED_DIGESTS: Readonly<Record<string, string>> = {
  // Published Sep 15 2026 (Thomas: live checkout now; later changes are new
  // versions, with notice to institutions).
  "enterprise-terms/2026-1": "09955d54cdf6c66f245f7f71da3dffb4e5f10e664e425c6d39bbda551301b43a",
  "data-sharing-addendum/2026-1": "b74701a2c3e7f2b8cb6aa00b8235b2d59d88623c358e78bfac2156753b329b30",
  // Published Sep 17 2026 (Thomas: the roster of who has joined, going
  // forward, with the consent on the join screen and in P3's own Terms).
  "enterprise-terms/2026-2": "cc66f7257ad8ae6fcf82e96f8946c6139253604c7a0073f2a2d72f12bf7e29dc",
  "data-sharing-addendum/2026-2": "1cb14561b7dd573b908cc8b8e0c8edee8dd48de71d987ce1e3f3f809e65e7d93",
};

export function documentsOf(id: DocumentId): readonly LegalDocument[] {
  return DOCUMENTS.filter((doc) => doc.id === id);
}

/** The version a new acceptance is made against: the newest one. */
export function currentVersion(id: DocumentId): LegalDocument {
  const doc = documentsOf(id)[0];
  if (!doc) throw new Error(`no versions of ${id}`);
  return doc;
}

export function findDocument(id: DocumentId, slug: string): LegalDocument | null {
  return documentsOf(id).find((doc) => doc.slug === slug) ?? null;
}

export function isDocumentId(value: string): value is DocumentId {
  return value === "enterprise-terms" || value === "data-sharing-addendum";
}

/* ==========================================================================
   7. The checkout pane's excerpt
   ========================================================================== */

/** The Addendum sections the checkout pane shows. The operative part. */
export const DSA_PANE_REFS: readonly string[] = ["I", "II", "III", "V", "VII", "X", "XII"];

function clauseBody(clause: Clause): readonly string[] {
  const out: string[] = [];
  if (clause.text) out.push(clause.heading && !clause.ref ? `${clause.heading}. ${clause.text}` : clause.text);
  for (const item of clause.items ?? []) out.push(item);
  return out;
}

/** The pane's clauses, projected from the document so they cannot drift from it. */
export function paneClauses(doc: LegalDocument, refs: readonly string[] = DSA_PANE_REFS): readonly DocumentClause[] {
  return refs.map((ref) => {
    const section = doc.sections.find((s) => s.ref === ref);
    if (!section) throw new Error(`${doc.id} ${doc.version} has no section ${ref}`);
    return {
      ref: section.ref,
      heading: section.heading,
      body: section.clauses.flatMap(clauseBody),
    };
  });
}

/* ==========================================================================
   8. Canonical text and its digest
   ========================================================================== */

/**
 * The exact text that is hashed. Deterministic, whitespace-normalised, and
 * independent of the HTML around it, so the same document hashes the same in
 * the Worker, in a browser and in a test.
 */
export function plainText(doc: LegalDocument): string {
  const lines: string[] = [];
  lines.push(doc.title);
  lines.push(`Version ${doc.version}`);
  lines.push(doc.effectiveDate ? `Effective ${doc.effectiveDate}` : "Draft. Not in force.");
  lines.push("");
  for (const p of doc.preamble) lines.push(p);
  for (const section of doc.sections) {
    lines.push("");
    lines.push(`${section.ref}. ${section.heading}`);
    for (const clause of section.clauses) {
      const head = [clause.ref, clause.heading ? `${clause.heading}.` : null].filter(Boolean).join(" ");
      lines.push(head ? `${head} ${clause.text}`.trim() : clause.text);
      for (const item of clause.items ?? []) lines.push(`- ${item}`);
    }
  }
  lines.push("");
  for (const p of doc.closing) lines.push(p);
  return lines.map((l) => l.replace(/\s+/g, " ").trimEnd()).join("\n");
}

/** SHA-256 of `plainText`, lowercase hex. The field an acceptance record stores. */
export async function digest(doc: LegalDocument): Promise<string> {
  const bytes = new TextEncoder().encode(plainText(doc));
  const hash = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(hash), (b) => b.toString(16).padStart(2, "0")).join("");
}

/* ==========================================================================
   9. The subprocessor list's change log

   The list itself is SUBPROCESSORS in pricing.ts, rendered on /trust and at
   /legal/subprocessors from the one constant. This is its dated history,
   newest first, which the Addendum promises alongside the list.
   ========================================================================== */

export interface SubprocessorChange {
  readonly date: string;
  readonly body: string;
}

export const SUBPROCESSOR_CHANGES: readonly SubprocessorChange[] = [
  {
    date: "September 7, 2026",
    body: "Added Cloudflare, Anthropic, Algolia, Resend and Stripe. Each processes personal data today, and none appeared on the previous schedule.",
  },
  {
    date: "May 2026",
    body: "First schedule, in the Data Sharing Agreement reviewed with counsel: Amazon Web Services, Google (Firebase), Amplitude, Retool, Twilio (SendGrid), Intercom, Sentry and 3Advance.",
  },
];
